/* ──────────────────────────────────────────────────────────────
   Búsqueda interactiva de disponibilidad de profesores
   (ConsultaDisponibilidadPage.tsx)
   ────────────────────────────────────────────────────────────── */

import { useMemo, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/* Material UI */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';

/* Componentes propios */
import WeekPicker from '../../components/WeekPicker';
import FormularioFormacion from './FormularioFormacion';
import SadCreateDialog from '../../components/sad/SadCreateDialog';
import { SAD_FEATURE_ENABLED } from '../../config/features';

/* Hooks */
import { useAsignaturas } from '../../hooks/useAsignaturas';
import { useCentros } from '../../hooks/useCentros';
import { useFormatos } from '../../hooks/useFormatos';
import { useProfesores } from '../../hooks/useProfesores';
import { useIdiomas } from '../../hooks/useIdiomas';
import {
  ResultadoKo,
  ResultadoOk,
  useAvailability,
  WeekIssue,
  useAvailabilityFormables,
  SearchMode,
} from '../../hooks/useAvailability';

/* API + toast */
import api from '../../api/axios';
import { toast } from 'react-toastify';

/* ───────────── plugins ───────────── */
dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/* ───────────────── helpers fechas ───────────────── */
const weekToMonday = (wk: string) => {
  const [y, w] = wk.split('-').map(Number);
  return dayjs(`${y}-01-04`)
    .startOf('isoWeek')
    .add(w - 1, 'week');
};
const mondayToIso = (d: dayjs.Dayjs) =>
  `${d.isoWeekYear()}-${String(d.isoWeek()).padStart(2, '0')}`;

/* ───────────────── tipos internos ───────────────── */
type ModeProfesor = 'disponible' | 'formable';
type SearchModeUI = SearchMode;
type BulbColor = 'green' | 'amber' | 'red' | 'disabled';

/* ───────────────── helpers semáforo ───────────────── */
const COLOR_FOR: Record<BulbColor, string> = {
  green: 'success.main',
  amber: 'warning.main',
  red: 'error.main',
  disabled: 'grey.300',
};

/* ═════════════════ COMPONENTE PRINCIPAL ═════════════════ */
export default function ConsultaDisponibilidadPage() {
  /* —— i18n —— */
  const { t, i18n } = useTranslation<'translation'>();
  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  /* —— router —— */
  const navigate = useNavigate();

  /* —— diálogo formación —— */
  const [formDialog, setFormDialog] = useState<{
    open: boolean;
    idProfesor?: number;
  }>({ open: false });

  /* —— diálogo SAD (nuevo) —— */
  const [sadOpen, setSadOpen] = useState(false);

  /* —— catálogos —— */
  const { rows: asignaturas } = useAsignaturas();
  const { rows: centros } = useCentros();
  const { rows: formatos } = useFormatos();
  const { rows: profesores } = useProfesores();
  const { rows: idiomas } = useIdiomas();

  /* —— formulario —— */
  const todayMon = dayjs().startOf('isoWeek');
  const initialSW = mondayToIso(todayMon);
  const initialEW = mondayToIso(todayMon.add(4, 'week'));

  const [searchMode, setSearchMode] = useState<SearchModeUI>('FLEXIBLE');
  const [form, setForm] = useState({
    startWeek: initialSW,
    endWeek: initialEW,
    asignaturaId: 0,
    centroId: '',
    formatoId: '',
    profesorId: '',
    idiomaId: '',
    diasSemana: [0, 1, 2, 3, 4], // L-V (0=Lunes ... 6=Domingo)
    horaDesde: '16:00',
    horaHasta: '20:00',
    duracionMin: 60,
    clasesSemana: 1,
  });

  const isStrict = searchMode === 'STRICT';

  /* —— handlers —— */
  const handleChange = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const nextValue =
      k === 'asignaturaId' || k === 'duracionMin' || k === 'clasesSemana'
        ? Number(value)
        : value;

    setForm({ ...form, [k]: nextValue } as typeof form);
  };

  /* —— rango real de fechas —— */
  const monday = weekToMonday(form.startWeek);
  const sunday = weekToMonday(form.endWeek).add(6, 'day');
  const startDate = monday.format('YYYY-MM-DD');
  const endDate = sunday.format('YYYY-MM-DD');

  /* —— payload para hooks —— */
  const params = useMemo(() => {
    if (!form.asignaturaId) return undefined;
    const {
      centroId,
      formatoId,
      profesorId,
      idiomaId,
      startWeek,
      endWeek,
      duracionMin,
      clasesSemana,
      ...rest
    } = form;
    return {
      ...rest,
      duracionMin: Number(duracionMin) || 0,
      startDate,
      endDate,
      centroId: centroId ? Number(centroId) : undefined,
      formatoId: formatoId ? Number(formatoId) : undefined,
      profesorId: profesorId ? Number(profesorId) : undefined,
      idiomaId: idiomaId ? Number(idiomaId) : undefined, // hoy no filtra (solo UI)
      clasesSemana: Number(clasesSemana) || 1,
      searchMode,
    } as const;
  }, [form, startDate, endDate, searchMode]);

  /* —— consultas —— */
  const { ok: resultados, descartados, loading, error } = useAvailability(params);
  const {
    ok: formables,
    loading: loadingFormables,
    error: errorFormables,
  } = useAvailabilityFormables(resultados.length === 0 ? params : undefined);

  /* —— helpers —— */
  const motivoLabel = (m: WeekIssue['reason'] | ResultadoKo['motivo']) =>
    t(`availability.motivo.${m}`);

  const goToReserva = (idProfesor: number) => {
    navigate('/reservas/nueva', {
      state: {
        initialValues: {
          idProfesor,
          idAsignatura: form.asignaturaId || undefined,
          idCentro: form.centroId ? Number(form.centroId) : undefined,
          idFormato: form.formatoId ? Number(form.formatoId) : undefined,
        },
      },
    });
  };

  const openFormacion = (idProfesor: number) => setFormDialog({ open: true, idProfesor });

  const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
  const toggleDia = (d: number) => () => {
    const s = new Set(form.diasSemana);
    s.has(d) ? s.delete(d) : s.add(d);
    setForm({ ...form, diasSemana: [...s].sort() });
  };

  const isDayActive = (wk: string, d: number) => {
    const date = weekToMonday(wk).add(d, 'day');
    return date.isSameOrAfter(startDate, 'day') && date.isSameOrBefore(endDate, 'day');
  };

  const formatWeek = (wk: string) => {
    const mon = weekToMonday(wk);
    const sun = mon.add(6, 'day');
    return mon.month() === sun.month()
      ? `${t('availability.week')} ${mon.format('DD')}-${sun.format('DD MMMM')}`
      : `${t('availability.week')} ${mon.format('DD MMM')} – ${sun.format('DD MMM')}`;
  };

  /* —— semáforo —— */
  const Semaforo = ({
    weekKey,
    status,
  }: {
    weekKey: string;
    status: ('green' | 'amber' | 'red')[];
  }) => (
    <Stack direction="row" spacing={0.25} sx={{ ml: 1 }}>
      {status.map((c, i) => {
        const active = isDayActive(weekKey, i);
        const color: BulbColor = active ? c : 'disabled';
        return (
          <Box
            key={i}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: COLOR_FOR[color],
              outline: '1px solid rgba(0,0,0,.25)',
            }}
          />
        );
      })}
    </Stack>
  );

  const formatSlot = (iniStr: string) => {
    const ini = dayjs(`1970-01-01T${iniStr}`);
    const fin = ini.add(form.duracionMin, 'minute');
    return `${ini.format('HH:mm')}-${fin.format('HH:mm')}`;
  };

  /* —— helpers para render de detalles —— */
  const strictSlotsForWeek = (wk: string) => {
    const mon = weekToMonday(wk);
    const [h, m] = form.horaDesde.split(':').map(Number);
    // Un slot por cada día seleccionado
    return form.diasSemana
      .filter((d) => isDayActive(wk, d))
      .map((d) => {
        const ini = mon.add(d, 'day').hour(h).minute(m).second(0).millisecond(0);
        const fin = ini.add(Number(form.duracionMin || 0), 'minute');
        return { ini, fin, day: d };
      });
  };

  const slotFits = (intervalos: ResultadoOk['intervalos'], ini: dayjs.Dayjs, fin: dayjs.Dayjs) =>
    intervalos.some((it) => {
      const a = dayjs(it.ini);
      const b = dayjs(it.fin);
      return ini.isSameOrAfter(a) && fin.isSameOrBefore(b);
    });

  /* —— valores por defecto para el nuevo dialogo SAD —— */
  const weeksApprox =
    dayjs(endDate).startOf('isoWeek').diff(dayjs(startDate).startOf('isoWeek'), 'week') + 1;
  const initialSesiones = Math.max(1, weeksApprox) * Math.max(1, Number(form.clasesSemana || 1));
  const initialHorasTotales = (initialSesiones * Number(form.duracionMin || 0)) / 60;

  /* —— render profesores —— */
  const renderProfesores = (lista: ResultadoOk[], mode: ModeProfesor) => (
    <Paper sx={{ p: 1 }}>
      {lista.map((prof) => {
        /* agrupa intervalos por semana ISO */
        const byWeek = new Map<string, ResultadoOk['intervalos']>();
        prof.intervalos.forEach((it) => {
          const wk = `${dayjs(it.ini).isoWeekYear()}-${String(dayjs(it.ini).isoWeek()).padStart(
            2,
            '0',
          )}`;
          (byWeek.get(wk) ?? byWeek.set(wk, []).get(wk)!).push(it);
        });

        // ordenar por inicio dentro de cada semana para que salgan 16:00–18:00, 19:30–21:00, etc.
        for (const [wk, arr] of byWeek.entries()) {
          arr.sort((a, b) => dayjs(a.ini).diff(dayjs(b.ini)));
          byWeek.set(wk, arr);
        }

        const weeksWithIssues = new Set(prof.unavailableWeeks.map((x) => x.week));
        const weekKeys = [...new Set([...byWeek.keys(), ...weeksWithIssues])].sort();

        return (
          <Accordion key={prof.profesorId} disableGutters sx={{ mb: 0.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {/* —— nombre + ratio global —— */}
              <Typography
                variant="subtitle1"
                sx={{ color: weeksWithIssues.size ? 'error.main' : 'inherit' }}
              >
                {profesores.find((p) => p.id === prof.profesorId)?.nombre ?? `#${prof.profesorId}`}
              </Typography>

              <Chip
                size="small"
                sx={{ ml: 1 }}
                label={`${prof.ratio.toFixed(1)} %`}
                color={prof.ratio < 85 ? 'warning' : 'default'}
              />

              {/* —— patrón elegido (si lo hay) —— */}
              {prof.pattern?.length > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1 }}
                  label={prof.pattern
                    .map(
                      (p) =>
                        `${t('common.weekLettersShort.' + DAY_KEYS[p.day])} ${formatSlot(p.time)}`,
                    )
                    .join(' · ')}
                />
              )}

              {/* —— sugerencias (máx. 3) —— */}
              {prof.suggestions?.slice(0, 3).map((pat, i) => (
                <Chip
                  key={i}
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{ ml: 0.5 }}
                  label={pat
                    .map(
                      (p) =>
                        `${t('common.weekLettersShort.' + DAY_KEYS[p.day])} ${formatSlot(p.time)}`,
                    )
                    .join(' · ')}
                />
              ))}

              {/* —— ratio del patrón óptimo (solo flexible) —— */}
              {searchMode === 'FLEXIBLE' && (
                <Chip
                  size="small"
                  sx={{ ml: 1 }}
                  label={`${prof.patternRatio.toFixed(1)} %`}
                  color={prof.patternRatio < 85 ? 'warning' : 'success'}
                />
              )}

              {/* —— semáforo global —— */}
              {weekKeys.length > 0 && <Semaforo weekKey={weekKeys[0]} status={prof.dayStatus} />}

              {/* —— botón reservar / solicitar formación —— */}
              <Button
                variant="contained"
                size="small"
                sx={{ ml: 'auto' }}
                onClick={() =>
                  mode === 'disponible'
                    ? goToReserva(prof.profesorId)
                    : openFormacion(prof.profesorId)
                }
              >
                {mode === 'disponible'
                  ? t('availability.button.reserve')
                  : t('availability.button.requestTraining')}
              </Button>
            </AccordionSummary>

            {/* —— detalles por semana —— */}
            <AccordionDetails sx={{ p: 0 }}>
              {weekKeys.map((wk) => {
                const intervalos = byWeek.get(wk) ?? [];
                const issues = prof.unavailableWeeks.filter((x) => x.week === wk);

                return (
                  <Accordion key={wk} disableGutters sx={{ bgcolor: 'grey.50', mb: 0.25 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        variant="body2"
                        sx={{ color: issues.length ? 'error.main' : 'inherit' }}
                      >
                        {formatWeek(wk)}
                      </Typography>

                      <Semaforo weekKey={wk} status={prof.dayStatus} />

                      {issues.map((iss) => (
                        <Chip
                          key={iss.reason}
                          size="small"
                          sx={{ ml: 1 }}
                          label={motivoLabel(iss.reason)}
                          color="error"
                        />
                      ))}
                    </AccordionSummary>

                    <AccordionDetails sx={{ pl: 3, pt: 0 }}>
                      {/* ESTRICTO: solo mostrar los slots exactos solicitados que caben */}
                      {isStrict ? (
                        (() => {
                          const requested = strictSlotsForWeek(wk);
                          const okSlots = requested.filter(({ ini, fin }) =>
                            slotFits(intervalos, ini, fin),
                          );
                          if (okSlots.length === 0) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                {t('availability.noSlots')}
                              </Typography>
                            );
                          }
                          return (
                            <Stack spacing={0.3}>
                              {okSlots.map((s, i) => (
                                <Typography key={i} variant="body2">
                                  {s.ini.format('ddd DD/MM HH:mm')} – {s.fin.format('HH:mm')}
                                </Typography>
                              ))}
                            </Stack>
                          );
                        })()
                      ) : // FLEXIBLE: mostrar los huecos reales que quedan en la ventana
                      intervalos.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t('availability.noSlots')}
                        </Typography>
                      ) : (
                        <Stack spacing={0.3}>
                          {intervalos.map((it, idx) => (
                            <Typography key={idx} variant="body2">
                              {dayjs(it.ini).format('ddd DD/MM HH:mm')} –{' '}
                              {dayjs(it.fin).format('HH:mm')}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Paper>
  );

  /* ═════════════════ RENDER ═════════════════ */
  return (
    <Stack spacing={3}>
      {/* —— BOTÓN SUPERIOR: Solicitar SAD —— */}
      {SAD_FEATURE_ENABLED && (
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setSadOpen(true)}>
            Solicitar SAD
          </Button>
        </Stack>
      )}

      {/* —— FILTROS —— */}
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {(['startWeek', 'endWeek'] as const).map((k, i) => (
            <Grid item xs={6} key={k}>
              <WeekPicker
                label={t(`availability.filter.${i === 0 ? 'start' : 'end'}`)}
                value={form[k]}
                onChange={(val) => setForm({ ...form, [k]: val })}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label={t('availability.filter.subject')}
              value={form.asignaturaId}
              onChange={handleChange('asignaturaId')}
            >
              <MenuItem value={0}>
                <em>{t('common.chooseOption')}</em>
              </MenuItem>
              {asignaturas.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Centro / Formato / Idioma */}
          {(
            [
              { k: 'centroId', rows: centros, label: 'center' },
              { k: 'formatoId', rows: formatos, label: 'format' },
              { k: 'idiomaId', rows: idiomas, label: 'language' },
            ] as const
          ).map(({ k, rows, label }) => (
            <Grid item xs={4} key={k}>
              <TextField
                select
                fullWidth
                label={t(`availability.filter.${label}`)}
                value={form[k]}
                onChange={handleChange(k as any)}
                /* ⬇️ CAMBIO: ya no mostramos “languageIgnored” (el backend sí filtra) */
                helperText={undefined}
              >
                <MenuItem value="">
                  <em>{t('common.any')}</em>
                </MenuItem>
                {rows.map((r: any) => (
                  <MenuItem key={r.id} value={r.id}>
                    {'nombreFormato' in r ? r.nombreFormato : r.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ))}

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label={t('availability.filter.teacher')}
              value={form.profesorId}
              onChange={handleChange('profesorId')}
            >
              <MenuItem value="">
                <em>{t('common.any')}</em>
              </MenuItem>
              {profesores.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((key, i) => (
              <FormControlLabel
                key={key}
                label={t(`common.weekLetters.${key}`)}
                control={<Checkbox checked={form.diasSemana.includes(i)} onChange={toggleDia(i)} />}
              />
            ))}
          </Grid>

          {/* —— inputs de tiempo/duración —— */}
          <Grid item xs={4}>
            <TextField
              fullWidth
              type="time"
              InputLabelProps={{ shrink: true }}
              label={t(
                isStrict ? 'availability.filter.time.startExact' : 'availability.filter.time.from',
              )}
              value={form.horaDesde}
              onChange={handleChange('horaDesde')}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              fullWidth
              type="time"
              InputLabelProps={{ shrink: true }}
              label={t('availability.filter.time.to')}
              value={form.horaHasta}
              onChange={handleChange('horaHasta')}
              disabled={isStrict}
              helperText={isStrict ? t('availability.hint.strictIgnoresWindow') : undefined}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              fullWidth
              type="number"
              InputLabelProps={{ shrink: true }}
              label={t('availability.filter.minDuration')}
              inputProps={{ min: 15 }}
              value={form.duracionMin}
              onChange={handleChange('duracionMin')}
            />
          </Grid>

          {/* clases/semana solo en FLEXIBLE */}
          {!isStrict && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label={t('availability.filter.classesPerWeek')}
                inputProps={{ min: 1 }}
                value={form.clasesSemana}
                onChange={handleChange('clasesSemana')}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <RadioGroup
              row
              value={searchMode}
              onChange={(_, v) => setSearchMode(v as SearchModeUI)}
            >
              <FormControlLabel
                value="STRICT"
                control={<Radio />}
                label={t('availability.mode.strict')}
              />
              <FormControlLabel
                value="FLEXIBLE"
                control={<Radio />}
                label={t('availability.mode.flexible')}
              />
            </RadioGroup>
          </Grid>
        </Grid>
      </Paper>

      {/* —— RESULTADOS —— */}
      {loading && <CircularProgress sx={{ alignSelf: 'center' }} />}
      {error && <Alert severity="error">{(error as any).message}</Alert>}

      {!loading && !error && resultados.length === 0 && (
        <Alert severity="warning">{t('availability.noTeachers')}</Alert>
      )}

      {!loading && resultados.length > 0 && renderProfesores(resultados, 'disponible')}

      {resultados.length === 0 && (
        <>
          {loadingFormables && <CircularProgress sx={{ alignSelf: 'center' }} />}
          {errorFormables && <Alert severity="error">{(errorFormables as any).message}</Alert>}
          {!loadingFormables && !errorFormables && formables.length > 0 && (
            <>
              <Alert severity="info" sx={{ mb: 1 }}>
                {t('availability.showingTrainingCandidates')}
              </Alert>
              {renderProfesores(formables, 'formable')}
            </>
          )}
        </>
      )}

      {!loading && !error && descartados.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
            {t('availability.discarded')}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {descartados.map((d) => (
            <Typography key={d.profesorId} variant="body2" sx={{ mb: 0.5 }}>
              {profesores.find((p) => p.id === d.profesorId)?.nombre ?? `#${d.profesorId}`}
              {' — '} {motivoLabel(d.motivo)} ({d.ratio.toFixed(1)} %)
            </Typography>
          ))}
        </Paper>
      )}

      {/* —— DIALOG: Solicitar SAD (nuevo componente) —— */}
      {SAD_FEATURE_ENABLED && (
        <SadCreateDialog
          open={sadOpen}
          onClose={() => setSadOpen(false)}
          onCreate={async (dto) => {
            try {
              const { data: created } = await api.post('/sad', dto);
              await api.post(`/sad/${created.id}/send`);
              toast.success('SAD enviada al profesor');
            } catch (e) {
              // Interceptor ya muestra el toast; re-lanzamos para que el diálogo no se cierre.
              throw e;
            }
          }}
          initial={{
            tipo: 'continua',
            profesorUserId: form.profesorId ? Number(form.profesorId) : undefined,
            // proyectoId: lo seleccionará el usuario en el diálogo
            asignaturaId: form.asignaturaId || undefined,
            formatoId: form.formatoId ? Number(form.formatoId) : undefined,
            centroId: form.centroId ? Number(form.centroId) : undefined,
            idioma: '',

            periodoInicio: startDate,
            periodoFin: endDate,
            diasSemana: form.diasSemana.map((d0a6) => d0a6 + 1), // 0..6 → 1..7
            franjas: form.diasSemana.map((d0a6) => ({
              diaSemana: d0a6 + 1,
              horaInicio: form.horaDesde,
              horaFin: form.horaHasta,
            })),
            sesiones: initialSesiones,
            horasTotales: initialHorasTotales,
          }}
        />
      )}

      {/* —— DIALOG: Solicitar formación —— */}
      <Dialog
        open={formDialog.open}
        onClose={() => setFormDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <Stack p={2}>
          <FormularioFormacion
            preset={{
              idAsignatura: form.asignaturaId || undefined,
              idProfesor: formDialog.idProfesor,
              fechaLimite: startDate,
            }}
            onClose={() => setFormDialog({ open: false })}
          />
        </Stack>
      </Dialog>
    </Stack>
  );
}
